import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ActivityIcon, AlertTriangleIcon, ClipboardCheckIcon, FileClockIcon, HeadphonesIcon, LayoutGridIcon, RocketIcon, ShieldAlertIcon, UsersIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { ScopeSwitcher } from '../../components/admin/ScopeSwitcher';
import { KpiCard } from '../../components/admin/KpiCard';
import { WidgetBoundary } from '../../components/admin/Widget';
import { dashboardWidgets } from '../../components/admin/widgets/registry';
import { WidgetLayoutDialog } from '../../components/admin/WidgetLayoutDialog';
import { useWidgetLayout } from '../../hooks/useWidgetLayout';
import { useGovernance } from '../../contexts/GovernanceContext';
import { useTickets } from '../../contexts/TicketContext';
import { allCampuses, evaluateLaunch, scopeCovers } from '../../utils/governance';
import { auditInScope } from '../../utils/auditScope';

/**
 * Admin home answers one question: what needs my attention?
 *
 * Every row is permission-aware and scope-aware, so two administrators signing
 * in see genuinely different work rather than the same list with disabled rows.
 */
export function AdminHome() {
  const {
    approvals,
    staff,
    invitations,
    switches,
    audit,
    launchChecks,
    tenants,
    activeScope,
    allows,
    me,
    scopeLabel
  } = useGovernance();
  const {
    tickets
  } = useTickets();
  const navigate = useNavigate();
  const [arranging, setArranging] = React.useState(false);
  const widgetIds = React.useMemo(() => dashboardWidgets.map(widget => widget.id), []);
  const layout = useWidgetLayout(widgetIds);
  const widgetById = React.useMemo(() => new Map(dashboardWidgets.map(widget => [widget.id, widget])), []);
  const pendingApprovals = approvals.filter(request => request.state === 'requested' && scopeCovers(activeScope, request.scope));
  const canSeeApprovals = allows('staff.approve').allowed || allows('payouts.approve').allowed;
  const expiredAccess = staff.filter(member => member.assignments.some(assignment => assignment.state === 'active' && assignment.expiresAt && new Date(assignment.expiresAt) < new Date()));
  const noMfa = staff.filter(member => member.status === 'active' && !member.mfaEnabled);
  const pendingInvites = invitations.filter(invitation => invitation.status === 'pending');
  const expiredInvites = invitations.filter(invitation => invitation.status === 'expired');
  const paused = switches.filter(item => item.state === 'paused');
  const openTickets = tickets.filter(ticket => ticket.status !== 'Resolved' && ticket.status !== 'Closed');
  const deniedAttempts = audit.filter(record => record.result === 'denied');
  const activeStaff = staff.filter(member => member.status === 'active');
  const deniedInScope = deniedAttempts.filter(record => auditInScope(record.scope, activeScope, scopeLabel(activeScope)));

  // Fourteen daily counts of refused attempts, counted from the audit log. Not a
  // trend line — a shape, beside a number that is the actual value.
  const midnight = new Date();
  midnight.setHours(0, 0, 0, 0);
  const deniedSeries = Array.from({
    length: 14
  }, (_, index) => {
    const day = new Date(midnight);
    day.setDate(midnight.getDate() - (13 - index));
    const next = new Date(day);
    next.setDate(day.getDate() + 1);
    return deniedInScope.filter(record => {
      const at = new Date(record.at);
      return at >= day && at < next;
    }).length;
  });
  const launchBlocked = allCampuses(tenants).filter(({
    tenant,
    campus
  }) => scopeCovers(activeScope, {
    level: 'campus',
    tenantId: tenant.id,
    campusId: campus.id,
    institutionId: tenant.institutions.find(institution => institution.campuses.some(item => item.id === campus.id))?.id
  })).map(({
    campus
  }) => evaluateLaunch(campus, launchChecks)).filter(outcome => outcome.campus.status !== 'live' && outcome.blockers.length > 0);
  const attention = [canSeeApprovals && pendingApprovals.length > 0 ? {
    id: 'approvals',
    tone: 'warn' as const,
    icon: ClipboardCheckIcon,
    title: `${pendingApprovals.length} request${pendingApprovals.length === 1 ? '' : 's'} waiting on an approver`,
    detail: pendingApprovals.map(request => request.summary).join(' · '),
    to: '/admin/governance',
    action: 'Open approvals'
  } : null, launchBlocked.length > 0 ? {
    id: 'launch',
    tone: 'danger' as const,
    icon: RocketIcon,
    title: `${launchBlocked.length} campus${launchBlocked.length === 1 ? '' : 'es'} cannot go live`,
    detail: launchBlocked.map(outcome => `${outcome.campus.name}: ${outcome.blockers.length} blocker${outcome.blockers.length === 1 ? '' : 's'}`).join(' · '),
    to: '/admin/launch',
    action: 'Open launch control'
  } : null, expiredAccess.length > 0 ? {
    id: 'expired',
    tone: 'warn' as const,
    icon: ShieldAlertIcon,
    title: `${expiredAccess.length} staff assignment${expiredAccess.length === 1 ? '' : 's'} past expiry`,
    detail: 'Access already stopped working. The records still need reviewing or renewing so the person is not silently locked out.',
    to: '/admin/governance',
    action: 'Open access review'
  } : null, noMfa.length > 0 ? {
    id: 'mfa',
    tone: 'warn' as const,
    icon: ShieldAlertIcon,
    title: `${noMfa.length} active administrator without MFA`,
    detail: noMfa.map(member => member.handle).join(' · '),
    to: '/admin/governance',
    action: 'Open staff'
  } : null, deniedAttempts.length > 0 ? {
    id: 'denied',
    tone: 'neutral' as const,
    icon: FileClockIcon,
    title: `${deniedAttempts.length} refused administrative attempt${deniedAttempts.length === 1 ? '' : 's'}`,
    detail: 'Refusals are logged like successes. Repeated attempts on the same record are worth a look.',
    to: '/admin/audit',
    action: 'Open audit log'
  } : null, openTickets.length > 0 ? {
    id: 'tickets',
    tone: 'brand' as const,
    icon: ActivityIcon,
    title: `${openTickets.length} open support ticket${openTickets.length === 1 ? '' : 's'}`,
    detail: openTickets.map(ticket => ticket.subject).slice(0, 2).join(' · '),
    to: '/admin/support',
    action: 'Open support desk'
  } : null].filter(Boolean) as {
    id: string;
    tone: 'warn' | 'danger' | 'brand' | 'neutral';
    icon: typeof RocketIcon;
    title: string;
    detail: string;
    to: string;
    action: string;
  }[];
  return <div className="space-y-6" data-id="element-5783" data-mp="37nqk5sky2j3x">
      <PageHeader eyebrow="Administration" title="What needs attention" description="Scoped to your operating context. Nothing here is a summary of the whole platform unless your access covers the whole platform." actions={<>
            <Button variant="secondary" size="sm" icon={<LayoutGridIcon className="h-4 w-4" data-id="element-5786" data-mp="2kpnb9dmj3iro" />} onClick={() => setArranging(true)} data-id="element-5785" data-mp="2knkabyju8586">
              Arrange dashboard
            </Button>
            <Badge tone="ok" plain data-id="element-5787" data-mp="iwfk96xjzcpy">
              Production environment
            </Badge>
          </>} data-id="element-5784" data-mp="37mylu9zyq79y" />

      {/* Counted from the records this administrator can actually see, so a
          scoped administrator is never shown a platform-wide figure they have no
          authority over. Nothing here is a stored metric. */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-5788" data-mp="2x3h0riewep4u">
        <KpiCard label="Waiting on an approver" value={canSeeApprovals ? pendingApprovals.length : undefined} meaning={canSeeApprovals ? 'Requests in your scope that cannot proceed until someone other than the requester decides.' : 'Your roles do not include an approval permission, so this is not yours to see.'} icon={ClipboardCheckIcon} tone={pendingApprovals.length ? 'warn' : 'neutral'} to={canSeeApprovals ? '/admin/governance' : undefined} linkLabel="Open approvals" data-id="element-5789" data-mp="2x1xmfxcsyo5p" />
        <KpiCard label="Administrators" value={activeStaff.length} meaning={`Active staff records. ${noMfa.length} without MFA.`} icon={UsersIcon} tone={noMfa.length ? 'warn' : 'ok'} to="/admin/governance" linkLabel="Open access review" data-id="element-5790" data-mp="cbf6ayvwt529" />
        <KpiCard label="Open support tickets" value={openTickets.length} meaning="Raised by people using the platform and not yet resolved or closed." icon={HeadphonesIcon} tone={openTickets.length ? 'brand' : 'ok'} to="/admin/support" linkLabel="Open support desk" data-id="element-5791" data-mp="onr0obv4n4vw" />
        <KpiCard label="Refused attempts" value={deniedInScope.length} meaning="Actions the authorization engine refused. Recorded exactly like successes." icon={ShieldAlertIcon} tone={deniedInScope.length ? 'warn' : 'ok'} series={deniedSeries} to="/admin/audit" linkLabel="Open audit log" data-id="element-5792" data-mp="z7vw6bada8k" />
      </div>

      <Panel title="Your access" description="A role says what you may do. A scope says where. They are evaluated separately." data-id="element-5793" data-mp="1magb9a3waptd">
        <ScopeSwitcher data-id="element-5794" data-mp="2daj48ebkfv1a" />
        <dl className="mt-5 grid gap-4 border-t border-line pt-4 sm:grid-cols-3" data-id="element-5795" data-mp="2iax3b6v6vun6">
          <div data-id="element-5796" data-mp="2n25cc1slxdzt">
            <dt className="text-body-sm text-ink-muted" data-id="element-5797" data-mp="2n1n9gmbfw1ix">Signed in as</dt>
            <dd className="mt-0.5 text-body font-semibold text-ink" data-id="element-5798" data-mp="2n4j8kdz0ww8y">
              {me.name} <span className="cz-num font-normal text-ink-muted" data-id="element-5799" data-mp="2r2z4j7d31eyi">{me.handle}</span>
            </dd>
          </div>
          <div data-id="element-5800" data-mp="1ufug13c19z8t">
            <dt className="text-body-sm text-ink-muted" data-id="element-5801" data-mp="1zcb11onxrxho">Active assignments</dt>
            <dd className="mt-0.5 text-body font-semibold text-ink" data-id="element-5802" data-mp="1zeff2zfjucvo">
              {me.assignments.filter(assignment => assignment.state === 'active').length}
            </dd>
          </div>
          <div data-id="element-5803" data-mp="28b68t56mpfn3">
            <dt className="text-body-sm text-ink-muted" data-id="element-5804" data-mp="3etcumsc3x3qg">Looking at</dt>
            <dd className="mt-0.5 text-body font-semibold text-ink" data-id="element-5805" data-mp="3et36zbejb8nx">{scopeLabel(activeScope)}</dd>
          </div>
        </dl>
      </Panel>

      {attention.length === 0 ? <Panel title="Nothing outstanding" data-id="element-5806" data-mp="3tv1javouhciu">
          <EmptyState icon={<ClipboardCheckIcon className="h-5 w-5" data-id="element-5808" data-mp="3owrpopirqjzc" />} title="No work waiting on you" description="Nothing in your scope needs an approval, review or decision right now." data-id="element-5807" data-mp="3ty7bijsrsuis" />
        </Panel> : <ul className="grid gap-4 lg:grid-cols-2" data-id="element-5809" data-mp="3g4g8zvdgiawk">
          {attention.map(item => {
        const Icon = item.icon;
        return <li key={item.id} data-id="element-5810" data-mp="3671u4076lu6d">
                <Panel bodyClassName="p-5" data-id="element-5811" data-mp="365qnztq9kac3">
                  <div className="flex items-start gap-3" data-id="element-5812" data-mp="3694tvby4z1oa">
                    <span aria-hidden className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-canvas text-ink-muted" data-id="element-5813" data-mp="318gthzo0p8he">
                      <Icon className="h-5 w-5" data-id="element-5814" data-mp="zfsnxjnwi8yg" />
                    </span>
                    <div className="min-w-0" data-id="element-5815" data-mp="vfs8pwsz9tqc">
                      <div className="flex flex-wrap items-center gap-2" data-id="element-5816" data-mp="vcwb69l1l72f">
                        <p className="text-body-lg font-semibold text-ink" data-id="element-5817" data-mp="2ow4yckgz74mp">{item.title}</p>
                        {item.tone === 'danger' && <Badge tone="danger" icon={<AlertTriangleIcon className="h-4 w-4" data-id="element-5819" data-mp="2fxsx7oihgog0" />} data-id="element-5818" data-mp="2fx1evfdwcn7o">
                            Blocker
                          </Badge>}
                      </div>
                      <p className="mt-1.5 max-w-[62ch] text-body-sm text-ink-muted" data-id="element-5820" data-mp="37npegmzx9kj8">{item.detail}</p>
                      <Button variant="secondary" size="sm" className="mt-3" onClick={() => navigate(item.to)} data-id="element-5821" data-mp="3clza0ypk6n5d">
                        {item.action}
                      </Button>
                    </div>
                  </div>
                </Panel>
              </li>;
      })}
        </ul>}

      {/* Rendered from the widget register in this administrator's own order.
          Each widget owns its loading, empty and error state, and is wrapped so
          one failure cannot take the console down with it. */}
      {layout.visible.length === 0 ? <Panel title="No panels shown" data-id="element-5822" data-mp="1tom53uutgs95">
          <EmptyState icon={<LayoutGridIcon className="h-5 w-5" data-id="element-5824" data-mp="1opj0ucmnuq28" />} title="Every dashboard panel is hidden" description="You have hidden all of them in this browser. Nothing is missing from the platform." action={<Button variant="secondary" onClick={layout.reset} data-id="element-5825" data-mp="w1n6vfoh7c95">
                Restore defaults
              </Button>} data-id="element-5823" data-mp="1tm9s7w3x182y" />
        </Panel> : <div className="grid gap-4 xl:grid-cols-3" data-id="element-5826" data-mp="15y9qy28z9za4">
          {layout.visible.map(id => {
        const widget = widgetById.get(id);
        if (!widget) return null;
        return <div key={id} className={widget.span === 2 ? 'xl:col-span-2' : undefined} data-id="element-5827" data-mp="dam9ux6x1qs8">
                <WidgetBoundary title={widget.title} data-id="element-5828" data-mp="3codbmybgkds8">{widget.render()}</WidgetBoundary>
              </div>;
      })}
        </div>}

      <WidgetLayoutDialog open={arranging} onClose={() => setArranging(false)} widgets={dashboardWidgets} layout={layout} data-id="element-5829" data-mp="3hnpwjdirras6" />

      <div className="grid gap-5 lg:grid-cols-2" data-id="element-5830" data-mp="3qg1k8rmi0k4g">
        <Panel title="Paused capabilities" description="Paused, never deleted. Existing records continue through controlled recovery." bodyClassName="p-0" data-id="element-5831" data-mp="3qhmiar80ul72">
          {paused.length === 0 ? <p className="px-5 py-4 text-body-sm text-ink-muted" data-id="element-5832" data-mp="97o53cbvl42k">Nothing is currently paused.</p> : <ul className="divide-y divide-line" data-id="element-5833" data-mp="d9hyr0nnl7t8">
              {paused.map(item => <li key={item.id} className="px-5 py-3.5" data-id="element-5834" data-mp="2az9lboubi97p">
                  <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-5835" data-mp="2b0ugngoghw6m">
                    <p className="text-body font-medium text-ink" data-id="element-5836" data-mp="2f0mry158igfo">{item.label}</p>
                    <Badge tone="warn" data-id="element-5837" data-mp="27hlx048i1v3x">Paused</Badge>
                  </div>
                  <p className="mt-1 max-w-[62ch] text-body-sm text-ink-muted" data-id="element-5838" data-mp="22pvgthmvsd4l">{item.effect}</p>
                </li>)}
            </ul>}
        </Panel>

        <Panel title="Invitations" bodyClassName="p-0" data-id="element-5839" data-mp="1tpfi0lh31u62">
          <ul className="divide-y divide-line" data-id="element-5840" data-mp="1tr0c69zhjb3e">
            <li className="flex items-center justify-between px-5 py-3.5" data-id="element-5841" data-mp="2qffzdzf4h8dc">
              <p className="text-body text-ink" data-id="element-5842" data-mp="2qdbzrto9aviv">Pending acceptance</p>
              <p className="cz-num text-body-lg font-semibold text-ink" data-id="element-5843" data-mp="2lh4rvvcav9ya">{pendingInvites.length}</p>
            </li>
            <li className="flex items-center justify-between px-5 py-3.5" data-id="element-5844" data-mp="2hd6ubp2mi77c">
              <p className="text-body text-ink" data-id="element-5845" data-mp="2cf5a30tgo1ua">Expired without acceptance</p>
              <p className="cz-num text-body-lg font-semibold text-ink" data-id="element-5846" data-mp="1604ljg6rmlxq">{expiredInvites.length}</p>
            </li>
          </ul>
          <p className="border-t border-line px-5 py-3.5 text-body-sm text-ink-muted" data-id="element-5847" data-mp="10z6vkhfiknlo">
            An expired invitation grants nothing. The token is single-use and stops working on its
            expiry whether or not anyone revokes it.
          </p>
        </Panel>
      </div>
    </div>;
}